import { plans, type PricingPlan } from '@terramantle/marketing/content'

/**
 * Shared tier presentation. The card content (name, price, summary, bullets) is
 * the marketing site's `plans` in packages/terramantle.dev/src/lib/content.ts, so
 * the portal says exactly what the pricing page says. The server's `tier-config.ts`
 * (TIER_LIMITS) is still the source of truth for entitlements.
 */

export type Tier = 'starter' | 'team' | 'business' | 'enterprise'

/** Server tier keys, in the same column order as the site's `plans` (tiers.test.ts pins it). */
export const TIERS: readonly Tier[] = ['starter', 'team', 'business', 'enterprise']

export const TIER_PLAN = Object.fromEntries(TIERS.map((t, i) => [t, plans[i]])) as Record<Tier, PricingPlan>

export const TIER_LABEL = Object.fromEntries(TIERS.map((t) => [t, TIER_PLAN[t].name])) as Record<Tier, string>

/** "£24/org/mo". Solo's cadence is "Cloud" rather than a period, so it is the price alone. */
export const TIER_PRICE = Object.fromEntries(TIERS.map((t) => {
  const p = TIER_PLAN[t]
  return [t, p.cadence.startsWith('/') ? `${p.price}${p.cadence}` : p.price]
})) as Record<Tier, string>

/** The plan the pricing page features; the wizard pre-selects it. */
export const FEATURED_TIER: Tier = TIERS.find((t) => TIER_PLAN[t].featured) ?? 'starter'

/** Pence → "£3" / "£2.50". Seat prices are whole pounds today but the server sends pence. */
export function formatPence(pence: number): string {
  return pence % 100 === 0 ? `£${pence / 100}` : `£${(pence / 100).toFixed(2)}`
}

/**
 * What makes a member a billable seat. Mirrors the server's definition (worker
 * lib/memberActivity.ts, migration 132); shown wherever a seat count is.
 */
export const BILLABLE_USER_NOTE =
  'A member is billable for a month when they do anything in this org during that calendar month (UTC): ' +
  'sign in to the portal, use the API or CLI, publish or download a module or provider, or read or lock state with their own login. ' +
  'Idle members cost nothing. Bot tokens and CI identities never count as users.'

/** Tiers whose plan permits OPA policy ENFORCEMENT (mirrors TierLimits.opa). */
export const OPA_TIERS: ReadonlySet<Tier> = new Set<Tier>(['business', 'enterprise'])

/** Tiers that get the inline getting-started guide on an empty Modules / State / Providers page. */
export const GUIDED_ONBOARDING_TIERS: ReadonlySet<Tier> = new Set<Tier>(['business', 'enterprise'])
